// Public Helpdesk — listing + article pages at /p/helpdesk/:ident[/:slug].
// Standalone monochrome design with a single-icon theme toggle (system default
// on first visit; once the visitor picks a theme it's light or dark only —
// no way back to system without clearing the browser cache). Optional
// workspace branding (accent, per-theme fg/bg, title+body fonts).
// These pages never touch dashboard providers (no useAuth).

import { KORA_BRAND, resolveBrandColors } from '@/lib/brand-defaults';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  fetchPublicCenterThrough, fetchPublicCenterPage, fetchPublicArticle, searchPublicKb, votePublicKb, getPublicKbVote, subscribePublicKb,
  trackPublicKbView, kbVisitorUuid, kbMediaUrl,
  type PublicKbCenter, type PublicKbArticleSummary, type PublicKbArticle, type PublicKbSearchResult,
} from '@/lib/kb-public';
import { ArticleRenderer, extractHeadings } from '@/components/kb/renderer';
import { kbFontFamily, KB_FONTS } from '@/lib/kb-fonts';
import { setDocumentMeta, setCanonical, setOgImage, injectJsonLdList, buildBreadcrumbSchema } from '@/lib/seo';
import { IS_PRERENDER, useClientReady, readSnapshot, HydrationSnapshot, stableDate } from '@/lib/hydration';

// ── Theme (system default → toggle locks to light/dark) ──────────────────────

// `ready` is false during the hydration render: the prerendered HTML is always
// the light theme, so the stored/system theme applies only once mounted.
function useHcTheme(ready: boolean) {
  // localStorage stores 'light' | 'dark' | null. null = follow system.
  const [stored, setStored] = useState<'light' | 'dark' | null>(() => {
    try { return localStorage.getItem('hc-theme') as 'light' | 'dark' | null; } catch { return null; }
  });
  const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const fn = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  const dark = ready && (stored === null ? systemDark : stored === 'dark');
  const setTheme = useCallback((next: 'light' | 'dark') => {
    try { localStorage.setItem('hc-theme', next); } catch { /* private mode */ }
    setStored(next);
  }, []);
  return { dark, setTheme };
}

// ── Theme curtain transition ─────────────────────────────────────────────────
// to-dark: a dark sheet descends from the top, covers the page, the theme
// flips underneath at full cover, and the sheet vanishes (its color already
// matches the new background — seamless). to-light: a sheet matching the
// current dark background mounts invisibly, the theme flips under it, and the
// sheet rises back up like a stage curtain opening, revealing light from the
// bottom up.

type CurtainState = { dir: 'to-dark' | 'to-light'; bg: string };

function ThemeCurtain({ curtain, onCovered, onDone }: {
  curtain: CurtainState;
  onCovered: () => void;
  onDone: () => void;
}) {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[9998]"
      style={{
        backgroundColor: curtain.bg,
        transformOrigin: 'top',
        animation: curtain.dir === 'to-dark'
          ? 'hc-curtain-down 560ms cubic-bezier(0.76, 0, 0.24, 1) forwards'
          : 'hc-curtain-up 560ms cubic-bezier(0.76, 0, 0.24, 1) forwards',
      }}
      onAnimationEnd={() => {
        if (curtain.dir === 'to-dark') onCovered(); // flip while fully covered
        onDone();
      }}
    />
  );
}

// Shared effect styles (curtain, vote feedback, toast) — injected once.
if (typeof document !== 'undefined' && !document.getElementById('hc-fx-styles')) {
  const style = document.createElement('style');
  style.id = 'hc-fx-styles';
  style.textContent = `
    @keyframes hc-curtain-down { from { transform: scaleY(0); } to { transform: scaleY(1); } }
    @keyframes hc-curtain-up { from { transform: scaleY(1); } to { transform: scaleY(0); } }
    @keyframes hc-emoji-float {
      0% { opacity: 0; transform: translate(-50%, 6px) scale(0.4); }
      30% { opacity: 1; transform: translate(-50%, -12px) scale(1.2); }
      100% { opacity: 0; transform: translate(-50%, -40px) scale(1); }
    }
    .hc-vote-emoji {
      position: absolute; left: 50%; top: -8px; font-size: 18px; line-height: 1;
      pointer-events: none; z-index: 20; animation: hc-emoji-float 900ms ease-out forwards;
    }
    .hc-confetti-piece {
      position: absolute; left: 50%; top: 50%; width: 6px; height: 9px; border-radius: 1px;
      opacity: 0; animation: hc-confetti 850ms cubic-bezier(0.16, 0.6, 0.4, 1) forwards;
    }
    @keyframes hc-confetti {
      0% { opacity: 1; transform: translate(-50%, -50%) rotate(0deg); }
      100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)); }
    }
    @keyframes hc-toast-in { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
    .hc-toast-in { animation: hc-toast-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both; }
  `;
  document.head.appendChild(style);
}

function formatDate(iso: string | null, ready: boolean): string {
  if (!iso) return '';
  if (!ready) return stableDate(iso); // prerender + hydration render agree
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Declarative Google Fonts links — render with the page (not in an effect), so
// the prerendered HTML carries them and the workspace's fonts start loading
// with the document instead of after React mounts. React 18 keeps them inline;
// stylesheets work from the body and hydration renders the same tags.
function KbFontLinks({ branding }: { branding: PublicKbCenter['branding'] | null | undefined }) {
  const specs = [branding?.title_font, branding?.body_font]
    .filter((k): k is string => !!k && k !== 'system')
    .map((k) => KB_FONTS.find((f) => f.key === k)?.googleSpec)
    .filter((s): s is string => !!s);
  const unique = [...new Set(specs)];
  if (unique.length === 0) return null;
  const href = `https://fonts.googleapis.com/css2?family=${unique.join('&family=')}&display=swap`;
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="stylesheet" href={href} />
    </>
  );
}

// Hydration snapshot (see lib/hydration): the data the prerendered markup was
// built from, so the first client render reproduces it exactly.
const HC_SNAPSHOT_ID = 'hc-snapshot';
type PagingState = { page: number; total: number; hasMore: boolean; loadingMore: boolean };
interface HcSnapshot {
  key: string;
  center: PublicKbCenter;
  articles?: PublicKbArticleSummary[];
  article?: PublicKbArticle;
  paging?: PagingState;
}
function hcSnapshotKey(ident: string | undefined, slug: string | undefined, page: number): string {
  return slug ? `${ident}|${slug}` : `${ident}||p${page}`;
}
const trimSummary = (a: PublicKbArticleSummary): PublicKbArticleSummary => ({
  slug: a.slug, title: a.title, summary: a.summary, cover_key: a.cover_key, published_at: a.published_at,
  updated_at: a.updated_at, helpful_count: a.helpful_count, not_helpful_count: a.not_helpful_count, view_count: a.view_count,
});

export function PublicHelpCenterPage() {
  const { ident, slug } = useParams<{ ident: string; slug?: string }>();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get('embed') === '1';
  const navigate = useNavigate();
  const ready = useClientReady();
  const theme = useHcTheme(ready);

  // Seed from the prerendered snapshot when it was built for this exact URL.
  const urlPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const [seed] = useState(() => readSnapshot<HcSnapshot>(HC_SNAPSHOT_ID, hcSnapshotKey(ident, slug, urlPage)));
  const seededKey = useRef<string | null>(seed?.key ?? null);

  const [center, setCenter] = useState<PublicKbCenter | null>(seed?.center ?? null);
  const [articles, setArticles] = useState<PublicKbArticleSummary[]>(seed?.articles ?? []);
  const [article, setArticle] = useState<PublicKbArticle | null>(seed?.article ?? null);
  const [state, setState] = useState<'loading' | 'ready' | 'notfound'>(seed ? 'ready' : 'loading');
  // Step 4: paged listing ("Load more" appends; ?page=N deep links load 1..N)
  const [paging, setPaging] = useState<PagingState>(seed?.paging ?? { page: 1, total: 0, hasMore: false, loadingMore: false });
  // Step 3: a renamed article (or alias ident) answers with its current
  // ident/slug; keep that payload so the URL replace doesn't refetch.
  const redirected = useRef<{ key: string; data: Awaited<ReturnType<typeof fetchPublicArticle>> } | null>(null);

  // Theme curtain — explicit toggles animate; system changes and the initial
  // post-hydration application do not.
  const [curtain, setCurtain] = useState<CurtainState | null>(null);
  const darkBg = center?.branding?.dark_bg || KORA_BRAND.dark.bg;

  const handleThemeToggle = useCallback(() => {
    if (curtain) return; // one transition at a time
    const goingDark = !theme.dark;
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      theme.setTheme(goingDark ? 'dark' : 'light');
      return;
    }
    if (goingDark) {
      // The sheet descends over the light page; the theme flips at full cover.
      setCurtain({ dir: 'to-dark', bg: darkBg });
    } else {
      // The sheet matches the current dark page (invisible), the theme flips
      // under it immediately, then it rises away like an opening curtain.
      setCurtain({ dir: 'to-light', bg: darkBg });
      theme.setTheme('light');
    }
  }, [curtain, theme.dark, theme.setTheme, darkBg]);

  useEffect(() => {
    let alive = true;
    // Seeded from the snapshot: keep showing it and refresh quietly.
    const quiet = seededKey.current === hcSnapshotKey(ident, slug, urlPage);
    seededKey.current = null;
    if (!quiet) {
      setState('loading');
      setArticle(null);
    }
    const qsEmbed = embed ? 'embed=1' : '';
    const load: Promise<boolean> = slug
      ? (redirected.current?.key === `${ident}/${slug}` ? Promise.resolve(redirected.current.data) : fetchPublicArticle(ident!, slug)).then((r) => {
          redirected.current = null;
          if (!alive) return false;
          if (r.ident !== ident || r.article.slug !== slug) {
            // Old slug / alias: the API 301'd to the current article. Replace
            // the URL (canonical follows) instead of rendering a duplicate.
            redirected.current = { key: `${r.ident}/${r.article.slug}`, data: r };
            navigate(`/p/helpdesk/${r.ident}/${r.article.slug}${qsEmbed ? `?${qsEmbed}` : ''}`, { replace: true });
            return false;
          }
          setCenter(r.center); setArticle(r.article);
          return true;
        })
      : fetchPublicCenterThrough(ident!, parseInt(searchParams.get('page') || '1', 10)).then((r) => {
          if (!alive) return false;
          if (r.ident !== ident) {
            const qs = new URLSearchParams(searchParams);
            navigate(`/p/helpdesk/${r.ident}${qs.toString() ? `?${qs}` : ''}`, { replace: true });
            return false;
          }
          setCenter(r.center); setArticles(r.articles);
          setPaging({ page: r.page, total: r.total, hasMore: r.has_more, loadingMore: false });
          return true;
        });
    load.then((ok) => { if (alive && ok) setState('ready'); })
      .catch((err: { status?: number }) => {
        // A quiet refresh keeps the snapshot on transient failures.
        if (alive && (!quiet || err?.status === 404)) setState('notfound');
      });
    return () => { alive = false; };
    // searchParams/embed are read once per ident/slug on purpose: "Load more"
    // rewrites ?page= itself and must not trigger a full reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ident, slug]);

  const loadMore = useCallback(async () => {
    if (!ident || paging.loadingMore || !paging.hasMore) return;
    const next = paging.page + 1;
    setPaging((p) => ({ ...p, loadingMore: true }));
    try {
      const r = await fetchPublicCenterPage(ident, next);
      setArticles((prev) => {
        const seen = new Set(prev.map((a) => a.slug));
        return [...prev, ...r.articles.filter((a) => !seen.has(a.slug))];
      });
      setPaging({ page: r.page, total: r.total, hasMore: r.has_more, loadingMore: false });
      // Keep the position shareable/refresh-safe without a router navigation.
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('page', String(r.page));
        window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}`);
      } catch { /* non-browser */ }
    } catch {
      setPaging((p) => ({ ...p, loadingMore: false }));
    }
  }, [ident, paging]);

  // Beacon (one per page view)
  useEffect(() => {
    if (state !== 'ready' || !ident) return;
    trackPublicKbView(ident, `/p/helpdesk/${ident}${slug ? `/${slug}` : ''}`, slug, document.referrer || undefined);
    void kbVisitorUuid();
  }, [state, ident, slug]);

  const dark = theme.dark;
  const b = center?.branding;
  const { fg, bg, accent } = resolveBrandColors(b, dark);
  const titleFont = kbFontFamily(b?.title_font);
  const bodyFont = kbFontFamily(b?.body_font);

  // SEO
  useEffect(() => {
    document.querySelectorAll('script[type="application/ld+json"]').forEach((n) => n.remove());
  }, [state]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'strict-origin-when-cross-origin';
    document.head.appendChild(meta);
  }, []);

  useEffect(() => {
    if (state !== 'ready' || !center) return;
    if (article) {
      setDocumentMeta(article.meta.title || article.title, article.meta.description || article.summary || `${center.title} — ${article.title}`);
      setCanonical(`/p/helpdesk/${ident}/${article.slug}`);
      const cleanup = injectJsonLdList([
        {
          '@context': 'https://schema.org', '@type': 'Article',
          headline: article.title,
          description: article.meta.description || article.summary || undefined,
          datePublished: article.published_at || undefined,
          dateModified: article.updated_at || undefined,
          author: { '@type': 'Organization', name: center.workspace_name },
          publisher: { '@type': 'Organization', name: center.workspace_name },
          mainEntityOfPage: `https://usekora.app/p/helpdesk/${ident}/${article.slug}`,
        },
        buildBreadcrumbSchema([
          { name: center.title, url: `https://usekora.app/p/helpdesk/${ident}` },
          { name: article.title, url: `https://usekora.app/p/helpdesk/${ident}/${article.slug}` },
        ]),
      ]);
      setOgImage(article.cover_key
        ? kbMediaUrl(`/public/v1/kb/media/${encodeURIComponent(article.cover_key)}`)
        : '/assets/og-pricing.png');
      if (article.noindex) {
        const meta = document.querySelector('meta[name="robots"]') || (() => { const m = document.createElement('meta'); m.name = 'robots'; document.head.appendChild(m); return m; })();
        meta.setAttribute('content', 'noindex, nofollow');
      }
      return cleanup;
    }
    setDocumentMeta(center.meta.title || center.title, center.meta.description || `Articles and answers from ${center.workspace_name}`);
    // ?page=N renders pages 1..N, so it self-canonicalises (page 1 = base URL).
    const deepPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    setCanonical(deepPage > 1 ? `/p/helpdesk/${ident}?page=${deepPage}` : `/p/helpdesk/${ident}`);
    setOgImage('/assets/og-pricing.png');
    const cleanupList = injectJsonLdList([{
      '@context': 'https://schema.org', '@type': 'CollectionPage',
      name: center.meta.title || center.title,
      description: center.meta.description || undefined,
      hasPart: (articles ?? []).slice(0, 30).map((a) => ({
        '@type': 'Article', headline: a.title, url: `https://usekora.app/p/helpdesk/${ident}/${a.slug}`,
        datePublished: a.published_at || undefined,
      })),
    }]);
    return cleanupList;
  }, [state, center, article, ident]);

  const shell = useMemo(
    () => ({
      style: {
        backgroundColor: bg,
        color: fg,
        ...(bodyFont ? { fontFamily: bodyFont } : {}),
        ['--hc-accent' as string]: accent,
        // Explicit border palette — `border-current/[opacity]` classes are not
        // generated by Tailwind here, so those borders fell back to
        // currentColor (near-black). Cards use #cccccc in light theme.
        ['--hc-hairline' as string]: dark ? 'rgba(255,255,255,0.08)' : '#ececec',
        ['--hc-border' as string]: dark ? 'rgba(255,255,255,0.12)' : '#cccccc',
        ['--hc-line' as string]: dark ? 'rgba(255,255,255,0.22)' : 'rgba(24,24,27,0.22)',
        ['--hc-line-strong' as string]: dark ? 'rgba(255,255,255,0.4)' : 'rgba(24,24,27,0.4)',
        ['--hc-fill' as string]: dark ? 'rgba(255,255,255,0.10)' : 'rgba(24,24,27,0.08)',
      } as React.CSSProperties,
    }),
    [bg, fg, bodyFont, accent, dark],
  );

  return (
    <div className="min-h-screen" style={shell.style}>
      <KbFontLinks branding={b} />
      {curtain && (
        <ThemeCurtain
          curtain={curtain}
          onCovered={() => theme.setTheme('dark')}
          onDone={() => setCurtain(null)}
        />
      )}
      {state === 'ready' && center && (IS_PRERENDER || seed) && (
        <HydrationSnapshot
          id={HC_SNAPSHOT_ID}
          data={seed ?? ({
            key: hcSnapshotKey(ident, slug, urlPage),
            center,
            ...(slug ? { article: article ?? undefined } : { articles: articles.map(trimSummary), paging }),
          } satisfies HcSnapshot)}
        />
      )}
      {state === 'notfound' && <HcNotFound dark={dark} />}
      {state === 'ready' && center && !slug && (
        <ListingView
          ident={ident!} center={center} articles={articles} embed={embed} dark={dark}
          accent={accent} titleFont={titleFont} fg={fg}
          onThemeToggle={handleThemeToggle}
          total={paging.total} hasMore={paging.hasMore} loadingMore={paging.loadingMore}
          nextPage={paging.page + 1} onLoadMore={loadMore} ready={ready}
        />
      )}
      {state === 'ready' && center && slug && article && (
        <ArticleView
          ident={ident!} center={center} article={article} embed={embed} dark={dark}
          accent={accent} titleFont={titleFont}
          onThemeToggle={handleThemeToggle} ready={ready}
        />
      )}
    </div>
  );
}

// ── Theme toggle: single icon button ─────────────────────────────────────────

function ThemeToggle({ dark, onToggle }: { dark: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="inline-flex size-8 items-center justify-center rounded-full border border-[var(--hc-border)] text-current transition-colors hover:border-[var(--hc-line)]"
    >
      {dark ? (
        // Sun icon (click to go light)
        <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      ) : (
        // Moon icon (click to go dark)
        <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

function ThumbsUpIcon({ className }: { className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 0-3 3.88Z" /></svg>;
}

function ThumbsDownIcon({ className }: { className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2" /><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 0 3-3.88Z" /></svg>;
}

function relativeDate(iso: string | null, ready: boolean): string {
  if (!iso) return '';
  if (!ready) return stableDate(iso); // "now"-relative text can't match prerender
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > window.innerHeight * 2);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  if (!show) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Back to top"
      className="fixed bottom-6 right-6 z-40 flex size-10 items-center justify-center rounded-full border border-[var(--hc-border)] bg-[var(--hc-fill)] backdrop-blur transition-colors hover:border-[var(--hc-line)]">
      <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m18 15-6-6-6 6" /></svg>
    </button>
  );
}

function PoweredByKora() {
  return (
    <footer className="border-t border-[var(--hc-hairline)] px-6 py-6 text-center text-xs opacity-60">
      Powered by <a href="https://usekora.app" target="_blank" rel="noreferrer" className="font-semibold underline underline-offset-2">Kora</a> — the pay-as-you-go helpdesk
    </footer>
  );
}

function HcNotFound({ dark }: { dark: boolean }) {
  useEffect(() => {
    setDocumentMeta('Helpdesk not found', 'This helpdesk does not exist, is private, or has no published articles yet.');
    // setDocumentMeta resets robots to index,follow — a not-found page must never be indexed.
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute('content', 'noindex, nofollow');
  }, []);
  return (
    <div className={`flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center ${dark ? 'bg-zinc-950 text-zinc-100' : 'bg-white text-zinc-900'}`}>
      <p className="font-mono text-5xl font-bold opacity-15">404</p>
      <h1 className="text-xl font-semibold">Helpdesk not found</h1>
      <p className="max-w-sm text-sm opacity-60">This link doesn't match any helpdesk, the helpdesk is private, or it has no published articles yet.</p>
      <a href="https://usekora.app" className="mt-2 text-sm font-medium underline underline-offset-2 opacity-80 hover:opacity-100">usekora.app</a>
    </div>
  );
}

// ── Listing ──────────────────────────────────────────────────────────────────

function ListingView(props: {
  ident: string; center: PublicKbCenter; articles: PublicKbArticleSummary[];
  embed: boolean; dark: boolean; accent: string; titleFont?: string; fg: string;
  onThemeToggle: () => void;
  total: number; hasMore: boolean; loadingMore: boolean; nextPage: number; onLoadMore: () => void;
  ready: boolean;
}) {
  const { ident, center, articles, embed } = props;
  const totalCount = Math.max(props.total, articles.length);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PublicKbSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  // U-01: grid/list toggle (persisted per browser)
  const [view, setView] = useState<'grid' | 'list'>(() => {
    try { return (localStorage.getItem('hc-view') as 'grid' | 'list') || 'grid'; } catch { return 'grid'; }
  });
  // Prerender always renders the grid; the stored preference applies once mounted.
  const shownView = props.ready ? view : 'grid';
  const switchView = (v: 'grid' | 'list') => {
    try { localStorage.setItem('hc-view', v); } catch {}
    setView(v);
  };

  // Surfaces: the page sits on a quiet gray/near-black backdrop with the
  // content in one elevated card (workspace branding still drives the page
  // background when the owner set one).
  const dark = props.dark;
  const outerBg = dark ? (center.branding.dark_bg || '#050505') : (center.branding.light_bg || '#f4f4f5');
  const cardBg = dark ? '#0b0b0d' : '#ffffff';
  const hairline = dark ? 'rgba(255,255,255,0.08)' : '#ececec';
  const gridBorder = dark ? 'rgba(255,255,255,0.12)' : '#cccccc';

  useEffect(() => {
    if (!query.trim()) { setResults(null); return; }
    setSearching(true);
    const t = setTimeout(() => {
      searchPublicKb(ident, query.trim())
        .then((r) => setResults(r.results))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, ident]);

  const list = results ?? articles;

  // U-11: "/" focuses search; Escape clears
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        (document.querySelector('[data-search-box] input') as HTMLElement)?.focus();
      }
      if (e.key === 'Escape' && document.activeElement?.closest('[data-search-box]')) {
        setQuery('');
        (document.activeElement as HTMLElement)?.blur();
      }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  return (
    <div
      className={embed ? 'min-h-screen' : 'min-h-screen px-3 py-4 sm:px-6 sm:py-10'}
      style={{
        backgroundColor: outerBg,
        color: props.fg,
        backgroundImage: dark && !center.branding.dark_bg
          ? 'radial-gradient(1100px 600px at 50% -8%, rgba(255,255,255,0.07), transparent 60%)'
          : undefined,
      }}
    >
      <div
        className={`mx-auto flex w-full max-w-[780px] flex-col ${embed ? 'min-h-screen' : 'rounded-2xl border shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.05)]'}`}
        style={embed ? undefined : { backgroundColor: cardBg, borderColor: hairline }}
      >
        {!embed && (
          <header
            className="sticky top-0 z-20 flex items-center justify-between rounded-t-2xl border-b px-6 py-4 backdrop-blur sm:px-8"
            style={{
              borderColor: hairline,
              backgroundColor: dark ? 'rgba(11,11,13,0.86)' : 'rgba(255,255,255,0.86)',
            }}
          >
            {center.logo_url ? (
              <img src={center.logo_url} alt={center.workspace_name} className="h-8 w-auto rounded" />
            ) : (
              <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: props.titleFont }}>{center.workspace_name}</span>
            )}
            <ThemeToggle dark={props.dark} onToggle={props.onThemeToggle} />
          </header>
        )}
        {embed && (
          <div className="flex justify-end px-6 pt-4">
            <ThemeToggle dark={props.dark} onToggle={props.onThemeToggle} />
          </div>
        )}

        <div className="px-6 pb-2 pt-10 sm:px-10">
          <h1 className="max-w-xl text-4xl font-bold leading-[1.08] tracking-tight sm:text-[2.6rem]" style={{ fontFamily: props.titleFont }}>
            {center.title}
          </h1>
          {center.intro && <p className="mt-3 max-w-lg text-[15px] leading-relaxed opacity-60">{center.intro}</p>}
          <div className="mt-7 flex items-center gap-2 rounded-xl border border-[var(--hc-hairline)] px-4 py-2.5 transition-colors focus-within:border-[var(--hc-line)]" data-search-box>
            <svg className="size-4 shrink-0 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search articles"
              placeholder={`Search ${totalCount} article${totalCount === 1 ? '' : 's'}…`}
              className="w-full bg-transparent text-sm outline-none placeholder:opacity-40"
            />
            {searching && <span className="text-xs opacity-40">…</span>}
          </div>
          {/* U-01: grid/list toggle */}
          {!embed && (
            <div className="mt-3 flex w-fit rounded-full border p-0.5 text-[11px]" style={{ borderColor: hairline }}>
              {(['grid', 'list'] as const).map((v) => (
                <button key={v} onClick={() => switchView(v)}
                  className={`rounded-full px-3 py-1 font-medium capitalize transition-colors ${shownView === v ? 'bg-[var(--hc-fill)] font-semibold' : 'opacity-50 hover:opacity-80'}`}>
                  {v === 'grid' ? '▦' : '☰'} {v}
                </button>
              ))}
            </div>
          )}
        </div>

        <main className="flex-1 px-6 pb-10 pt-6 sm:px-10">
          {list.length === 0 ? (
            <>
              <p className="py-16 text-center text-sm opacity-50">
                {results ? `No articles match "${query}".` : 'No articles published yet — check back soon.'}
              </p>
              {results && center.support_url && (
                <div className="mx-auto mt-6 max-w-md rounded-2xl border p-5 text-center" style={{ borderColor: hairline }}>
                  <p className="text-sm font-semibold">Still stuck?</p>
                  <p className="mt-1 text-[13px] opacity-60">Get help from a real person.</p>
                  <a href={center.support_url} target="_blank" rel="noreferrer"
                    className="mt-3 inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white"
                    style={{ backgroundColor: 'var(--hc-accent)' }}>Contact support</a>
                </div>
              )}
            </>
          ) : shownView === 'grid' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {list.map((a) => (
                <Link key={a.slug} to={`/p/helpdesk/${ident}/${a.slug}`}
                  className="group flex flex-col rounded-xl border border-[var(--hc-border)] p-4 transition-colors hover:border-[var(--hc-line)]">
                  {'cover_key' in a && a.cover_key && (
                    <img src={kbMediaUrl(`/public/v1/kb/media/${encodeURIComponent(a.cover_key)}`)} alt="" loading="lazy"
                      className="mb-3 aspect-[2/1] w-full rounded-lg object-cover" />
                  )}
                  <p className="text-[15px] font-semibold leading-snug group-hover:underline group-hover:underline-offset-2">{a.title}</p>
                  {a.summary && <p className="mt-1.5 line-clamp-2 text-[13px] opacity-60">{a.summary}</p>}
                </Link>
              ))}
            </div>
          ) : (
            <div>
              {list.map((a, i) => (
                <Link key={a.slug} to={`/p/helpdesk/${ident}/${a.slug}`}
                  className="group block py-5 transition-opacity hover:opacity-80"
                  style={i < list.length - 1 ? { borderBottom: `1px solid ${hairline}` } : undefined}>
                  <p className="text-[17px] font-semibold leading-snug group-hover:underline group-hover:underline-offset-4" style={{ fontFamily: props.titleFont }}>
                    {a.title}
                  </p>
                  {a.summary && <p className="mt-1 line-clamp-2 text-sm leading-relaxed opacity-55">{a.summary}</p>}
                  <p className="mt-2 text-xs opacity-40">{formatDate(a.published_at, props.ready)}</p>
                </Link>
              ))}
            </div>
          )}

          {/* Step 4: a real link (crawlable ?page=N) that appends in place when JS runs.
              Hidden while searching — search already covers every article. */}
          {!results && props.hasMore && list.length > 0 && (
            <div className="mt-8 flex flex-col items-center gap-2">
              <a
                href={`/p/helpdesk/${ident}?page=${props.nextPage}${embed ? '&embed=1' : ''}`}
                rel="next"
                onClick={(e) => { e.preventDefault(); props.onLoadMore(); }}
                aria-busy={props.loadingMore}
                className="rounded-full border border-[var(--hc-hairline)] px-5 py-2 text-sm font-medium transition-colors hover:border-[var(--hc-line)]"
              >
                {props.loadingMore ? 'Loading…' : 'Load more articles'}
              </a>
              <p className="text-xs opacity-40">Showing {articles.length} of {totalCount}</p>
            </div>
          )}

          {center.subs_enabled && <SubscribeBox ident={ident} hairline={hairline} />}
        </main>

        {!embed && <PoweredByKora />}
      </div>
    </div>
  );
}

function SubscribeBox({ ident, hairline }: { ident: string; hairline: string }) {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await subscribePublicKb(ident, email);
      setDone(res.already ? "You're already subscribed — thanks!" : 'Subscribed! You\'ll get an email when new articles are published.');
      setEmail('');
    } catch (err) {
      setDone(err instanceof Error ? err.message : 'Subscription failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mx-auto mt-12 max-w-md rounded-2xl border p-5 text-center" style={{ borderColor: hairline }}>
      <p className="text-sm font-semibold">Get new articles by email</p>
      {done ? (
        <p className="mt-2 text-sm opacity-60">{done}</p>
      ) : (
        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input type="email" required aria-label="Email address" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="min-w-0 flex-1 rounded-lg border border-[var(--hc-hairline)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--hc-line)]" />
          <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off"
            aria-hidden="true" className="hidden" />
          <button type="submit" disabled={busy || !email.trim()}
            className="shrink-0 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--hc-accent)' }}>
            {busy ? '…' : 'Subscribe'}
          </button>
        </form>
      )}
    </section>
  );
}

// ── Article ──────────────────────────────────────────────────────────────────

const LIKE_EMOJIS = ['😊', '🥳', '❤️', '😍', '🙏', '✨', '💖', '🙌', '👍', '💫'];
const CONFETTI_COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#f97316'];

interface ConfettiBurst { id: number; pieces: Array<{ dx: number; dy: number; rot: number; color: string; delay: number }> }

function makeConfetti(): ConfettiBurst['pieces'] {
  return Array.from({ length: 14 }, (_, i) => ({
    dx: Math.round(-44 + Math.random() * 88),
    dy: Math.round(-12 - Math.random() * 46),
    rot: Math.round(-220 + Math.random() * 440),
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: Math.round(Math.random() * 60),
  }));
}

function VoteRing() {
  return (
    <svg className="size-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function ArticleView(props: {
  ident: string; center: PublicKbCenter; article: PublicKbArticle;
  embed: boolean; dark: boolean; accent: string; titleFont?: string;
  onThemeToggle: () => void; ready: boolean;
}) {
  const { ident, center, article, embed } = props;
  const [counts, setCounts] = useState({ helpful: article.helpful_count, not: article.not_helpful_count });
  const [myVote, setMyVote] = useState<boolean | null>(null);
  const [pending, setPending] = useState<'helpful' | 'not' | null>(null);
  const [burst, setBurst] = useState<{ id: number; emoji: string } | null>(null);
  const [confetti, setConfetti] = useState<ConfettiBurst | null>(null);
  const [apology, setApology] = useState(false);
  const [voteError, setVoteError] = useState(false);
  const toc = useMemo(() => extractHeadings(article.content).filter(h => h.level >= 2 && h.level <= 3), [article.content]);

  const voteCacheKey = `hc-vote:${ident}|${article.slug}`;

  // Restore this visitor's vote: instantly from the localStorage cache, then
  // reconcile against the server (covers cache misses and other browsers).
  useEffect(() => {
    if (!props.ready) return;
    try {
      const cached = localStorage.getItem(voteCacheKey);
      if (cached === 'true' || cached === 'false') setMyVote(cached === 'true');
    } catch { /* private mode */ }
    let alive = true;
    getPublicKbVote(ident, article.slug, kbVisitorUuid())
      .then((r) => {
        if (!alive) return;
        setMyVote(r.my_vote);
        setCounts({ helpful: r.helpful_count, not: r.not_helpful_count });
      })
      .catch(() => { /* cache state stands */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ready, ident, article.slug]);

  const persistVote = useCallback((v: boolean | null) => {
    try {
      if (v === null) localStorage.removeItem(voteCacheKey);
      else localStorage.setItem(voteCacheKey, String(v));
    } catch { /* private mode */ }
  }, [voteCacheKey]);

  const vote = useCallback(async (helpful: boolean) => {
    if (pending) return; // one request at a time — the ring covers the wait
    const wasMyVote = myVote;
    // Same button = remove the vote; other button = switch.
    const next: boolean | null = wasMyVote === helpful ? null : helpful;

    // Optimistic deltas — always correct now that the server can remove votes.
    setCounts((c) => {
      let h = c.helpful, n = c.not;
      if (wasMyVote === true) h--;
      if (wasMyVote === false) n--;
      if (next === true) h++;
      if (next === false) n++;
      return { helpful: Math.max(0, h), not: Math.max(0, n) };
    });
    setMyVote(next);
    // Random emoji on every like action (fresh or switch); removes stay quiet.
    if (next === true) {
      setBurst({ id: Date.now(), emoji: LIKE_EMOJIS[Math.floor(Math.random() * LIKE_EMOJIS.length)] });
    }
    setVoteError(false);
    setPending(helpful ? 'helpful' : 'not');
    try {
      const res = await votePublicKb(ident, article.slug, next, kbVisitorUuid());
      // Reconcile with authoritative counts.
      setCounts({ helpful: res.helpful_count, not: res.not_helpful_count });
      setMyVote(res.my_vote);
      persistVote(res.my_vote);
      // Celebrate only on a genuinely recorded action.
      if (res.my_vote === true) {
        setConfetti({ id: Date.now(), pieces: makeConfetti() });
        window.setTimeout(() => setConfetti(null), 1100);
      } else if (res.my_vote === false) {
        setApology(true);
      }
    } catch {
      // Roll the whole interaction back — nothing was recorded.
      setCounts({ helpful: counts.helpful, not: counts.not });
      setMyVote(wasMyVote);
      setVoteError(true);
      window.setTimeout(() => setVoteError(false), 3000);
    } finally {
      setPending(null);
    }
  }, [ident, article.slug, myVote, pending, counts, persistVote]);

  useEffect(() => {
    if (!apology) return;
    const t = window.setTimeout(() => setApology(false), 5000);
    return () => window.clearTimeout(t);
  }, [apology]);

  const share = (network: 'copy' | 'x' | 'linkedin') => {
    const url = `https://usekora.app/p/helpdesk/${ident}/${article.slug}`;
    if (network === 'copy') { void navigator.clipboard.writeText(url); return; }
    const text = encodeURIComponent(article.title);
    const link = network === 'x'
      ? `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`
      : `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(link, '_blank', 'noopener');
  };

  // Same card-on-backdrop frame as the listing page.
  const dark = props.dark;
  const outerBg = dark ? (center.branding.dark_bg || '#050505') : (center.branding.light_bg || '#f4f4f5');
  const cardBg = dark ? '#0b0b0d' : '#ffffff';
  const hairline = dark ? 'rgba(255,255,255,0.08)' : '#ececec';
  const [tocOpen, setTocOpen] = useState(false); // collapsed by default

  return (
    <div
      className={embed ? 'min-h-screen' : 'min-h-screen px-3 py-4 sm:px-6 sm:py-10'}
      style={{
        backgroundColor: outerBg,
        backgroundImage: dark && !center.branding.dark_bg
          ? 'radial-gradient(1100px 600px at 50% -8%, rgba(255,255,255,0.07), transparent 60%)'
          : undefined,
      }}
    >
      <div
        className={`mx-auto flex w-full max-w-[780px] flex-col ${embed ? 'min-h-screen' : 'rounded-2xl border shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.05)]'}`}
        style={embed ? undefined : { backgroundColor: cardBg, borderColor: hairline }}
      >
        {!props.embed && (
          <header
            className="sticky top-0 z-20 flex items-center justify-between rounded-t-2xl border-b px-6 py-4 backdrop-blur sm:px-8"
            style={{
              borderColor: hairline,
              backgroundColor: dark ? 'rgba(11,11,13,0.86)' : 'rgba(255,255,255,0.86)',
            }}
          >
            <Link to={`/p/helpdesk/${ident}`} className="text-sm font-semibold hover:underline">
              ← {center.title}
            </Link>
            <ThemeToggle dark={props.dark} onToggle={props.onThemeToggle} />
          </header>
        )}
        {props.embed && (
          <div className="flex justify-end px-6 pt-4">
            <ThemeToggle dark={props.dark} onToggle={props.onThemeToggle} />
          </div>
        )}

        <article className="flex-1 px-6 py-10 sm:px-10">
          {article.cover_key && (
            <img src={kbMediaUrl(`/public/v1/kb/media/${encodeURIComponent(article.cover_key)}`)} alt=""
              className="mb-8 aspect-[2/1] w-full rounded-2xl object-cover" />
          )}
          <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-4xl" style={{ fontFamily: props.titleFont }}>{article.title}</h1>
          <p className="mt-2 text-xs opacity-50">Updated {relativeDate(article.updated_at, props.ready)}</p>

          {/* U-07: TOC for articles with ≥3 headings — collapsible, closed by default */}
          {toc.length >= 3 && (
            <nav className="mt-6 rounded-xl border border-[var(--hc-border)]" aria-label="Table of contents">
              <button
                onClick={() => setTocOpen((o) => !o)}
                aria-expanded={tocOpen}
                className="flex w-full items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-wider opacity-60 transition-opacity hover:opacity-100"
              >
                On this page
                <svg className={`size-3.5 transition-transform duration-200 ${tocOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
              </button>
              <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={{ gridTemplateRows: tocOpen ? '1fr' : '0fr' }}>
                <div className="overflow-hidden">
                  <ul className="space-y-1 px-4 pb-4 text-sm">
                    {toc.map((h, i) => (
                      <li key={i} style={{ paddingLeft: `${(h.level - 2) * 12}px` }}>
                        <a href={`#${h.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth' }); }}
                          className="opacity-60 hover:opacity-100 hover:underline">{h.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </nav>
          )}

        <div className="mt-8">
          <ArticleRenderer doc={article.content} />
        </div>

        {/* Votes */}
        <div className="mt-10 flex items-center gap-3 rounded-2xl border border-[var(--hc-border)] px-5 py-4">
          <span className="text-sm font-medium">Was this helpful?</span>
          <div className="relative ml-auto flex items-center gap-2">
            <button onClick={() => vote(true)} aria-pressed={myVote === true}
              className={`relative flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all duration-200 active:scale-95 ${myVote === true ? 'border-[var(--hc-line-strong)] bg-[var(--hc-fill)] font-semibold' : 'border-[var(--hc-border)] hover:border-[var(--hc-line)]'}`}>
              <ThumbsUpIcon className="size-3.5" />
              {pending === 'helpful' ? <VoteRing /> : <span className="tabular-nums">{counts.helpful}</span>}
              {burst && (
                <span key={burst.id} className="hc-vote-emoji" onAnimationEnd={() => setBurst(null)} aria-hidden="true">{burst.emoji}</span>
              )}
              {confetti && (
                <span className="pointer-events-none absolute inset-0 z-10" aria-hidden="true">
                  {confetti.pieces.map((p, i) => (
                    <i key={i} className="hc-confetti-piece" style={{
                      ['--dx' as string]: `${p.dx}px`, ['--dy' as string]: `${p.dy}px`,
                      ['--rot' as string]: `${p.rot}deg`, background: p.color,
                      animationDelay: `${p.delay}ms`,
                    } as React.CSSProperties} />
                  ))}
                </span>
              )}
            </button>
            <button onClick={() => vote(false)} aria-pressed={myVote === false}
              className={`relative flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all duration-200 active:scale-95 ${myVote === false ? 'border-[var(--hc-line-strong)] bg-[var(--hc-fill)] font-semibold' : 'border-[var(--hc-border)] hover:border-[var(--hc-line)]'}`}>
              <ThumbsDownIcon className="size-3.5" />
              {pending === 'not' ? <VoteRing /> : <span className="tabular-nums">{counts.not}</span>}
            </button>
          </div>
        </div>
        {voteError && (
          <p className="mt-2 text-xs text-red-500" role="alert">Couldn't record your vote — check your connection and try again.</p>
        )}

        {/* Share */}
        <div className="mt-4 flex items-center gap-2 text-xs opacity-70">
          <span>Share</span>
          <button onClick={() => share('copy')} className="rounded-md border border-[var(--hc-border)] px-2 py-1 hover:border-[var(--hc-line)]">Copy link</button>
          <button onClick={() => share('x')} className="rounded-md border border-[var(--hc-border)] px-2 py-1 hover:border-[var(--hc-line)]">X</button>
          <button onClick={() => share('linkedin')} className="rounded-md border border-[var(--hc-border)] px-2 py-1 hover:border-[var(--hc-line)]">LinkedIn</button>
        </div>

        {/* U-12: Still stuck? (only when owner set a support link) */}
        {!embed && center.support_url && (
          <div className="mx-auto mt-10 max-w-md rounded-2xl border border-[var(--hc-border)] p-5 text-center">
            <p className="text-sm font-semibold">Still stuck?</p>
            <p className="mt-1 text-[13px] opacity-60">Can't find what you're looking for? Get help from a real person.</p>
            <a href={center.support_url} target="_blank" rel="noreferrer"
              className="mt-3 inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: 'var(--hc-accent)' }}>
              Contact support
            </a>
          </div>
        )}

          {/* More from this center */}
          {(article.more ?? []).length > 0 && (
            <section className="mt-12">
              <h2 className="text-sm font-semibold uppercase tracking-wider opacity-50">More from {center.workspace_name}</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {(article.more ?? []).map((m) => (
                  <Link key={m.slug} to={`/p/helpdesk/${ident}/${m.slug}`}
                    className="rounded-xl border border-[var(--hc-border)] p-3 transition-colors hover:border-[var(--hc-line)]">
                    <p className="text-sm font-medium leading-snug">{m.title}</p>
                    <p className="mt-1 text-xs opacity-40">{formatDate(m.published_at, props.ready)}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        {!embed && <PoweredByKora />}
      </div>

      {/* Dislike apology toast with a Get-support CTA */}
      {apology && (
        <div className="fixed inset-x-0 bottom-6 z-[9999] flex justify-center px-4" role="status">
          <div
            className="hc-toast-in flex items-center gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg"
            style={{
              backgroundColor: props.dark ? '#18181b' : '#ffffff',
              borderColor: props.dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
              color: props.dark ? '#fafafa' : '#18181b',
            }}
          >
            <span className="text-base" aria-hidden="true">🙏</span>
            <span className="opacity-80">Sorry about that — your feedback helps us improve this article.</span>
            {center.support_url && (
              <a href={center.support_url} target="_blank" rel="noreferrer"
                className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
                style={{ backgroundColor: 'var(--hc-accent)' }}>
                Get support
              </a>
            )}
            <button onClick={() => setApology(false)} className="ml-1 shrink-0 opacity-40 transition-opacity hover:opacity-80" aria-label="Dismiss">✕</button>
          </div>
        </div>
      )}

      {!embed && <BackToTop />}
    </div>
  );
}
